import { Currency } from '../models/serializable/currency.js';
import { Discount } from '../models/serializable/discounts/discount.js';
import { EventTicket } from '../models/serializable/event-ticket.js';
import {
  getCoinsPerCurrencyUnit,
  Price,
} from '../models/serializable/price.js';
import { TicketOrderDiscountMode } from './core/ticket-order-discount-mode.js';
import { DiscountHelper } from './discount-helper.js';

/** An online/offline ticket order or one GenericOrderTicketEntry. */
export interface TicketPricingGroup {
  readonly tickets: readonly EventTicket[];
  readonly discount: Discount | null;
}

export interface TicketPriceBreakdown {
  readonly ticket: EventTicket;
  readonly originalPrice: Price;
  readonly ticketDiscount: Price;
  readonly priceAfterTicketDiscount: Price;
  readonly allocatedGroupDiscount: Price;
  readonly finalPrice: Price;
}

/** Ticket prices only: excludes fees, supplements, products and donations. */
export interface TicketPricingBreakdown {
  readonly tickets: readonly TicketPriceBreakdown[];
  readonly originalTotal: Price;
  readonly ticketDiscountTotal: Price;
  readonly subtotal: Price;
  readonly groupDiscountTotal: Price;
  readonly total: Price;
}

export abstract class TicketPricing {
  /**
   * Validates a snapshot without recalculating discounts or allocation.
   * @param {TicketPriceBreakdown} pricing Snapshot belonging to its ticket.
   * @return {boolean} Whether all amounts and snapshot arithmetic are valid.
   */
  static isValidTicketBreakdown(pricing: TicketPriceBreakdown): boolean {
    const originalPrice = pricing.ticket.template.price;
    const {
      priceAfterTicketDiscount,
      finalPrice,
      ticketDiscount,
      allocatedGroupDiscount,
    } = pricing;
    const prices = [
      pricing.originalPrice,
      priceAfterTicketDiscount,
      finalPrice,
      ticketDiscount,
      allocatedGroupDiscount,
    ];

    if(!prices.every((price) => isValidPrice(price, originalPrice.currency))) {
      return false;
    }

    if(pricing.originalPrice.value !== originalPrice.value) {
      return false;
    }

    if(priceAfterTicketDiscount.value > originalPrice.value) {
      return false;
    }

    if(finalPrice.value > priceAfterTicketDiscount.value) {
      return false;
    }

    return (
      ticketDiscount.value ===
        originalPrice.value - priceAfterTicketDiscount.value &&
      allocatedGroupDiscount.value ===
        priceAfterTicketDiscount.value - finalPrice.value
    );
  }

  /**
   * Applies ticket discounts, then the order/entry discount once.
   * Consumes the group discount in ticket order, capped at each remaining price.
   * HUF allocations use whole forints, other currencies use cents.
   * Returns null for empty groups, mixed currencies or invalid monetary values.
   *
   * Pass an OnlineEventTicketOrder, EventTicketOrder,
   * or GenericOrderTicketEntry.
   * For serials: select pricing.tickets by ticket identity, then call
   * pool.getSerialNumber(ticket, { ticketPricing: selectedTicketPricing }).
   * Reuse the breakdown while issuing the group; do not recalculate subsets.
   * @param {TicketPricingGroup} group Order or ticket entry to price.
   * @param {TicketOrderDiscountMode} discountMode Discounts to include.
   * @return {?TicketPricingBreakdown} Prices, or null for invalid groups.
   */
  static calculate(
      group: TicketPricingGroup,
      discountMode: TicketOrderDiscountMode = 'Everything',
  ): TicketPricingBreakdown | null {
    if(group.tickets.length === 0) {
      return null;
    }

    const currency = group.tickets[0].template.price.currency;
    const ticketDiscounts = calculateTicketDiscounts(
        group.tickets,
        currency,
        discountMode,
    );

    if(ticketDiscounts == null) {
      return null;
    }

    const { originalTotal, subtotal } = ticketDiscounts;
    const groupDiscount = discountMode === 'Everything' ? group.discount : null;
    const discountedTotal = calculateDiscountedPrice(
        subtotal,
        groupDiscount,
        currency,
    );

    if(discountedTotal == null) {
      return null;
    }

    const groupDiscountTotal = subtotal.difference(discountedTotal);
    const tickets = allocateGroupDiscount(
        ticketDiscounts.tickets,
        groupDiscountTotal,
    );
    const total = tickets.reduce(
        (sum, ticket) => sum.add(ticket.finalPrice),
        Price.zero(currency),
    );

    return {
      tickets,
      originalTotal,
      ticketDiscountTotal: originalTotal.difference(subtotal),
      subtotal,
      groupDiscountTotal,
      total,
    };
  }
}

type TicketPriceBeforeGroupDiscount = Omit<
  TicketPriceBreakdown,
  'allocatedGroupDiscount' | 'finalPrice'
>;

interface TicketDiscountBreakdown {
  readonly tickets: readonly TicketPriceBeforeGroupDiscount[];
  readonly originalTotal: Price;
  readonly subtotal: Price;
}

// Price stores minor units. Allocate whole forints for HUF, cents otherwise.
function getAllocationUnit(currency: Currency): number {
  return currency === Currency.HUF ? getCoinsPerCurrencyUnit(currency) : 1;
}

function isValidPrice(price: Price, currency: Currency): boolean {
  return price != null &&
    price.currency === currency &&
    Number.isSafeInteger(price.value) &&
    price.value >= 0 &&
    price.value % getAllocationUnit(currency) === 0;
}

function calculateDiscountedPrice(
    originalPrice: Price,
    discount: Discount | null,
    currency: Currency,
): Price | null {
  if(!isValidPrice(originalPrice, currency)) {
    return null;
  }

  if(
    discount?.fixedPrice != null &&
    !isValidPrice(discount.fixedPrice, currency)
  ) {
    return null;
  }

  const discountedPrice = DiscountHelper.calculateDiscountedPriceForSingleItem(
      originalPrice,
      discount,
  );

  if(!isValidPrice(discountedPrice, currency)) {
    return null;
  }

  if(discountedPrice.value > originalPrice.value) {
    return null;
  }

  return discountedPrice;
}

function calculateTicketDiscounts(
    tickets: readonly EventTicket[],
    currency: Currency,
    discountMode: TicketOrderDiscountMode,
): TicketDiscountBreakdown | null {
  const pricedTickets: TicketPriceBeforeGroupDiscount[] = [];
  let originalTotal = Price.zero(currency);
  let subtotal = Price.zero(currency);

  for(const ticket of tickets) {
    const originalPrice = ticket.template.price;
    const discount = discountMode === 'None' ? null : ticket.discount;
    const priceAfterTicketDiscount = calculateDiscountedPrice(
        originalPrice,
        discount,
        currency,
    );

    if(priceAfterTicketDiscount == null) {
      return null;
    }

    pricedTickets.push({
      ticket,
      originalPrice,
      ticketDiscount: originalPrice.difference(priceAfterTicketDiscount),
      priceAfterTicketDiscount,
    });

    originalTotal = originalTotal.add(originalPrice);
    subtotal = subtotal.add(priceAfterTicketDiscount);
  }

  if(
    !isValidPrice(originalTotal, currency) ||
    !isValidPrice(subtotal, currency)
  ) {
    return null;
  }

  return { tickets: pricedTickets, originalTotal, subtotal };
}

function allocateGroupDiscount(
    tickets: readonly TicketPriceBeforeGroupDiscount[],
    groupDiscountTotal: Price,
): TicketPriceBreakdown[] {
  let remainingGroupDiscount = groupDiscountTotal.value;

  return tickets.map((ticket) => {
    const allocatedValue = Math.min(
        remainingGroupDiscount,
        ticket.priceAfterTicketDiscount.value,
    );
    const allocatedGroupDiscount = Price.withCurrencyFromUnits(
        groupDiscountTotal.currency,
        allocatedValue,
    );
    remainingGroupDiscount -= allocatedValue;

    return {
      ...ticket,
      allocatedGroupDiscount,
      finalPrice: ticket.priceAfterTicketDiscount.difference(
          allocatedGroupDiscount,
      ),
    };
  });
}
